/**
 * Admin PWA client-state cleanup (L51/L52).
 * Clears Cache Storage + unregisters service workers (shared-device / logout ON).
 * Also drops deploy-volatile session latches / IndexedDB so a stale shell cannot stick.
 */

import { ADMIN_CHUNK_RECOVERY_LATCH_KEY } from './adminChunkLoadRecovery.js';

const ADMIN_LAST_TENANT_KEY = 'admin_last_tenant_code';

/**
 * Best-effort IndexedDB names used by the admin PWA shell when `indexedDB.databases()`
 * is missing (older Safari / Firefox). Prefer `databases()` when available so unknown
 * DBs are still cleared. Today Workbox ExpirationPlugin (NetworkFirst navigate cache)
 * opens `workbox-expiration`; keep this list in sync if admin adds more IDB stores.
 */
export const ADMIN_PWA_KNOWN_INDEXED_DB_NAMES = ['workbox-expiration'] as const;

/** Max wait for SW controller release after unregister before navigation proceeds. */
export const ADMIN_PWA_SW_RELEASE_TIMEOUT_MS = 2_000;

export const ADMIN_PWA_SW_RELEASE_POLL_MS = 50;

export interface ClearAdminPwaClientStateOptions {
  /** When true (logout), also drop cold-start last-tenant bootstrap key. */
  readonly clearLastTenantKey?: boolean;
  /**
   * When true (default), poll until controller is null or no registrations remain
   * before returning — required so reload is not intercepted by a stale worker.
   */
  readonly waitForControllerRelease?: boolean;
  readonly swReleaseTimeoutMs?: number;
  /**
   * When true (default), clear deploy-volatile session latches + IndexedDB.
   * Does not clear auth tokens (AuthProvider owns those).
   */
  readonly clearDeployVolatileState?: boolean;
}

export interface WaitForServiceWorkerReleaseResult {
  readonly released: boolean;
  readonly hadController: boolean;
  readonly registrationCount: number;
}

export interface WaitForServiceWorkerControllerChangeResult {
  readonly activated: boolean;
}

async function clearAllCaches(): Promise<void> {
  if (typeof caches === 'undefined') {
    return;
  }
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  } catch {
    // Best-effort — logout / tenant switch must not throw
  }
}

function deleteIndexedDatabaseByName(name: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => {
        resolve();
      };
      request.onblocked = () => {
        resolve();
      };
    } catch {
      resolve();
    }
  });
}

/**
 * Resolves IndexedDB names to delete.
 * - Prefer `indexedDB.databases()` when present.
 * - Fallback: {@link ADMIN_PWA_KNOWN_INDEXED_DB_NAMES} when `databases()` is missing
 *   (do not early-return — known admin Workbox DBs must still be cleared).
 */
async function resolveIndexedDatabaseNamesToClear(): Promise<readonly string[]> {
  if (typeof indexedDB === 'undefined') {
    return [];
  }
  if (typeof indexedDB.databases === 'function') {
    const databases = await indexedDB.databases();
    return databases
      .map((entry) => entry.name)
      .filter((name): name is string => typeof name === 'string' && name.length > 0);
  }
  return [...ADMIN_PWA_KNOWN_INDEXED_DB_NAMES];
}

async function clearDeployVolatileClientState(): Promise<void> {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(ADMIN_CHUNK_RECOVERY_LATCH_KEY);
    }
  } catch {
    // ignore private mode
  }

  if (typeof indexedDB === 'undefined') {
    return;
  }
  try {
    const names = await resolveIndexedDatabaseNamesToClear();
    await Promise.all(names.map((name) => deleteIndexedDatabaseByName(name)));
  } catch {
    // Best-effort — older browsers / blocked IDB
  }
}

async function unregisterAllServiceWorkers(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch {
    // Best-effort
  }
}

function readServiceWorkerSnapshot(): {
  readonly hadController: boolean;
  readonly registrationCount: number;
} {
  if (!('serviceWorker' in navigator) || navigator.serviceWorker == null) {
    return { hadController: false, registrationCount: 0 };
  }
  return {
    hadController: navigator.serviceWorker.controller != null,
    registrationCount: 0,
  };
}

async function countServiceWorkerRegistrations(): Promise<number> {
  if (!('serviceWorker' in navigator) || navigator.serviceWorker == null) {
    return 0;
  }
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    return registrations.length;
  } catch {
    return 0;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Await the next `controllerchange` after SKIP_WAITING (proven activation), or timeout.
 * Same bounded-wait philosophy as {@link waitForServiceWorkerRelease}: callers that still
 * need a nuclear unregister+navigate should proceed when `activated` is false.
 */
export async function waitForServiceWorkerControllerChange(options?: {
  readonly timeoutMs?: number;
}): Promise<WaitForServiceWorkerControllerChangeResult> {
  if (!('serviceWorker' in navigator) || navigator.serviceWorker == null) {
    return { activated: true };
  }

  const timeoutMs = options?.timeoutMs ?? ADMIN_PWA_SW_RELEASE_TIMEOUT_MS;
  const sw = navigator.serviceWorker;

  return new Promise((resolve) => {
    let settled = false;
    const finish = (activated: boolean): void => {
      if (settled) {
        return;
      }
      settled = true;
      sw.removeEventListener('controllerchange', onControllerChange);
      window.clearTimeout(timeoutId);
      resolve({ activated });
    };

    const onControllerChange = (): void => {
      finish(true);
    };

    const timeoutId = window.setTimeout(() => {
      finish(false);
    }, timeoutMs);

    sw.addEventListener('controllerchange', onControllerChange);
  });
}

/**
 * Poll until no controlling worker and no registrations, or timeout.
 * Callers should fall through to the next escalation step when `released` is false.
 */
export async function waitForServiceWorkerRelease(options?: {
  readonly timeoutMs?: number;
  readonly pollMs?: number;
}): Promise<WaitForServiceWorkerReleaseResult> {
  const timeoutMs = options?.timeoutMs ?? ADMIN_PWA_SW_RELEASE_TIMEOUT_MS;
  const pollMs = options?.pollMs ?? ADMIN_PWA_SW_RELEASE_POLL_MS;
  const snapshot = readServiceWorkerSnapshot();
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const hadController =
      'serviceWorker' in navigator &&
      navigator.serviceWorker != null &&
      navigator.serviceWorker.controller != null;
    const registrationCount = await countServiceWorkerRegistrations();
    if (!hadController && registrationCount === 0) {
      return {
        released: true,
        hadController: snapshot.hadController,
        registrationCount: 0,
      };
    }
    await sleep(pollMs);
  }

  const registrationCount = await countServiceWorkerRegistrations();
  return {
    released: false,
    hadController:
      'serviceWorker' in navigator &&
      navigator.serviceWorker != null &&
      navigator.serviceWorker.controller != null,
    registrationCount,
  };
}

/**
 * Clears Cache Storage and unregisters SW.
 * Auth tokens are cleared by AuthProvider logout separately.
 */
export async function clearAdminPwaClientState(
  options: ClearAdminPwaClientStateOptions = {},
): Promise<WaitForServiceWorkerReleaseResult> {
  if (options.clearLastTenantKey === true) {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(ADMIN_LAST_TENANT_KEY);
      }
    } catch {
      // ignore quota / private mode
    }
  }

  const clearVolatile = options.clearDeployVolatileState !== false;
  await Promise.all([
    clearAllCaches(),
    unregisterAllServiceWorkers(),
    clearVolatile ? clearDeployVolatileClientState() : Promise.resolve(),
  ]);

  if (options.waitForControllerRelease === false) {
    const registrationCount = await countServiceWorkerRegistrations();
    return {
      released: true,
      hadController: readServiceWorkerSnapshot().hadController,
      registrationCount,
    };
  }

  return waitForServiceWorkerRelease({
    timeoutMs: options.swReleaseTimeoutMs,
  });
}
